import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/PublicHeader";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";

export default function GiveSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useI18n();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [info, setInfo] = useState({});

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    const intervalMs = 2000;

    if (!sessionId) {
      setState("failed");
      return;
    }

    async function poll() {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired" || attempts >= maxAttempts) {
          setState("failed");
          return;
        }
        setTimeout(poll, intervalMs);
      } catch (err) {
        if (attempts >= maxAttempts) setState("failed");
        else setTimeout(poll, intervalMs);
      }
    }
    poll();

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div>
      <PublicHeader />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-6 space-y-4"
      >
        <Card className="rounded-2xl shadow-sm ring-1 ring-black/5">
          <CardContent className="p-6 text-center space-y-3">
            {state === "checking" && (
              <>
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Loader2 className="h-7 w-7 animate-spin" />
                </div>
                <p className="text-sm text-muted-foreground" data-testid="give-status-checking">{t.give.checking}</p>
              </>
            )}
            {state === "paid" && (
              <>
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h1 className="font-serif-display text-xl font-semibold" data-testid="give-success-title">{t.give.successTitle}</h1>
                <p className="text-sm text-muted-foreground">{t.give.successBody}</p>
                {info?.amount_total !== undefined && (
                  <p className="font-serif-display text-2xl font-semibold text-primary" data-testid="give-success-amount">
                    {fmtMoney((info.amount_total || 0) / 100, info.currency || "usd")}
                  </p>
                )}
              </>
            )}
            {state === "failed" && (
              <>
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                  <AlertTriangle className="h-7 w-7" />
                </div>
                <h1 className="font-serif-display text-xl font-semibold" data-testid="give-failed-title">{t.give.failedTitle}</h1>
                <p className="text-sm text-muted-foreground">{t.give.failedBody}</p>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button asChild variant="outline" className="flex-1" data-testid="give-back-home">
            <Link to="/">{t.give.backHome}</Link>
          </Button>
          <Button asChild className="flex-1" data-testid="give-back-to-give">
            <Link to="/give">{t.give.backToGive}</Link>
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
